import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useCrefisaContext } from "@/app/contexts/CrefisaContext";
import { crefisaService } from "@/app/services/crefisaService";
import { useCrefisaStream } from "@/app/hooks/useCrefisaStream";
import { useEffect, useMemo, useState } from "react";
import { Simulacao } from "@/app/services/crefisaService/createSimulations";
import { useToast } from "@/view/components/ui/use-toast";
import { useConsentSelection } from "../hooks/useConsentSelection";

const getOffersModalSchema = z.object({});

type getOffersModalFormData = z.infer<typeof getOffersModalSchema>;

interface UseOffersModalProps {
  onContinue?: () => void;
}

export default function useOffersModal({ onContinue }: UseOffersModalProps) {
  const {
    simulationData,
    customerData,
    setCustomer,
    setProposal,
    setConsentBankAccount,
  } = useCrefisaContext();
  const { toast } = useToast();

  const [selectedInstallment, setSelectedInstallment] =
    useState<Simulacao | null>(null);
  const [manualRegistration, setManualRegistration] = useState("");
  const [manualRegistrationDigit, setManualRegistrationDigit] = useState("");
  const [netIncome, setNetIncome] = useState("");
  const [consentLoading, setConsentLoading] = useState(false);

  const getOffersModalForm = useForm<getOffersModalFormData>({
    resolver: zodResolver(getOffersModalSchema),
    mode: "onChange",
  });

  const { handleSubmit } = getOffersModalForm;

  const {
    data,
    steps,
    isStreaming: isFetching,
    isError,
    error,
    retry,
    cancel,
  } = useCrefisaStream({
    queryKey: [
      "crefisa-simulation",
      simulationData?.clientId,
      customerData?.cpf,
      simulationData?.isRefinancing ?? false,
    ],
    enabled: !!simulationData?.clientId && !!customerData?.cpf,
    request: ({ onStep, signal }) =>
      crefisaService.createSimulations(
        simulationData!.clientId!,
        {
          cpf: customerData!.cpf,
          isRefinancing: simulationData!.isRefinancing ?? false,
          phone: customerData!.phone,
          ...(customerData?.income?.codeReceiptDataId
            ? { codeReceiptDataId: customerData.income.codeReceiptDataId }
            : {}),
        },
        { onStep, signal },
      ),
  });

  useEffect(() => {
    if (!isError) return;

    const err = error as {
      message?: string;
      mensagem?: string;
    } | null;

    toast({
      title: "Erro ao gerar proposta",
      description:
        err?.mensagem ||
        err?.message ||
        "Não foi possível realizar a simulação.",
      variant: "destructive",
    });
  }, [isError, error, toast]);

  const installments = useMemo(
    () => data?.simulacoes ?? [],
    [data?.simulacoes],
  );

  // valor fixo definido pela API (statusPortalTransparencia); sem seletor
  const currentInstallmentValue =
    data?.selectedInstallment ??
    (Array.isArray(data?.installment) ? data.installment[0] : null) ??
    null;

  const borrowedValue = selectedInstallment?.valorSolicitado ?? 0;

  const hasRenda = !!data?.renda;
  const isNetIncomeIncomplete = !hasRenda && !netIncome;
  const isManualRegistrationValid =
    !!manualRegistration && !!manualRegistrationDigit;

  const submitModal = handleSubmit(() => {
    if (!selectedInstallment) return;

    if (!isManualRegistrationValid) {
      toast({
        title: "Preencha o NIS",
        description: "Informe o NIS e o dígito para prosseguir.",
        variant: "destructive",
      });
      return;
    }

    if (consentChoicePending) {
      toast({
        title: "Selecione o consentimento",
        description: "Escolha qual consentimento será utilizado para continuar.",
        variant: "destructive",
      });
      return;
    }

    if (requiresConsent && !isConsentAuthorized) {
      toast({
        title: "Consentimento obrigatório",
        description:
          "É obrigatório realizar o consentimento para finalizar a proposta deste cliente.",
        variant: "destructive",
      });
      return;
    }

    setCustomer({
      income: {
        ...customerData?.income,
        registration: manualRegistration,
        registrationDigit: manualRegistrationDigit,
        valueIncome: hasRenda ? data!.renda : Number(netIncome) / 100,
        codeBenefitSpecie: 0,
      },
    });

    setProposal({
      value: selectedInstallment.valorSolicitado,
      prazo: selectedInstallment.quantidadeParcelas,
      simulationId: selectedInstallment.idSimulacao,
      refinancingId: selectedInstallment.idContratoRefin,
    });
    onContinue?.();
  });

  useEffect(() => {
    setSelectedInstallment(installments[installments.length - 1] ?? null);
  }, [installments]);

  useEffect(() => {
    if (data?.renda) {
      setNetIncome(String(Math.round(data.renda * 100)));
    }
  }, [data?.renda]);

  // NIS/dígito sempre editáveis: pré-preenche com o retorno da API quando houver
  useEffect(() => {
    setManualRegistration(data?.matricula ?? "");
    setManualRegistrationDigit(data?.digitoMatricula ?? "");
  }, [data?.matricula, data?.digitoMatricula]);

  const requiresConsent = !!simulationData?.naoRecebeViaCrefisa;

  const {
    consentOptions,
    selectedConsent,
    selectedConsentId,
    selectConsent,
    applyVerifiedConsents,
    consentChoicePending,
  } = useConsentSelection({
    simulationConsents: data?.dadosConsentimento,
    setCustomer,
    setConsentBankAccount,
  });

  const authorizedConsent = selectedConsent
    ? {
        nomeBanco: selectedConsent.nomeBanco,
        agencia: selectedConsent.agencia,
        conta: selectedConsent.conta,
        digitoConta: selectedConsent.digitoConta,
      }
    : null;
  const isConsentAuthorized = !!authorizedConsent;

  const handleVerifyConsent = async () => {
    if (!simulationData?.clientId) return;

    try {
      setConsentLoading(true);

      const consent = await crefisaService.getConsent(simulationData.clientId);
      const items = (consent.dadosConsentimento ?? []).filter((i) => i.conta);

      if (!consent.consentimentoRealizado || items.length === 0) {
        toast({
          title: "Consentimento pendente",
          description: consent.mensagem || "Consentimento ainda não realizado",
        });
        return;
      }

      applyVerifiedConsents(items);

      toast({
        title: items.length > 1 ? "Consentimentos encontrados" : "Consentimento autorizado",
        description:
          items.length > 1
            ? "Selecione qual consentimento deseja utilizar."
            : consent.mensagem || "Consentimento autorizado pelo cliente",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao verificar consentimento",
        description: error?.message || "Erro ao verificar consentimento",
        variant: "destructive",
      });
    } finally {
      setConsentLoading(false);
    }
  };

  const handleCopyConsentLink = async () => {
    if (!data?.linkConsentimento) return;

    try {
      await navigator.clipboard.writeText(data.linkConsentimento);
      toast({
        title: "Link copiado",
        description:
          "Link do consentimento copiado para a área de transferência.",
      });
    } catch {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o link.",
        variant: "destructive",
      });
    }
  };

  const requestError = error as { message?: string; mensagem?: string } | null;

  const errorMessage = isError
    ? requestError?.mensagem ||
      requestError?.message ||
      "Não foi possível realizar a simulação."
    : !isFetching && data?.statusPortalTransparencia === "nao_encontrado"
      ? "Status do portal transparência não encontrado ou ausente"
      : !isFetching && data?.sucesso === false
        ? data?.mensagemLimiteTomado || "Não foi possível realizar a simulação."
        : null;

  return {
    form: getOffersModalForm,
    submitModal,
    isPending: isFetching,
    isFetching,
    steps,
    retry,
    cancel,
    isError,
    installments,
    selectedInstallment,
    setSelectedInstallment,
    borrowedValue,
    commission: data?.commission,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    netIncome,
    setNetIncome,
    hasRenda,
    isNetIncomeIncomplete,
    isManualRegistrationValid,
    linkConsentimento: data?.linkConsentimento,
    handleCopyConsentLink,
    errorMessage,
    currentInstallmentValue,
    consentOptions,
    selectedConsentId,
    selectConsent,
    consentChoicePending,
    requiresConsent,
    authorizedConsent,
    isConsentAuthorized,
    consentLoading,
    handleVerifyConsent,
  };
}
